/**
 * Settings menu component.
 * Displays player-editable settings organized by category.
 *
 * This is an example implementation showing how to use the config system.
 * You can customize the UI to match your design system.
 */

import { useState, useEffect, useRef } from 'react';
import { useSettingsStore } from '@shared/stores/settingsStore';
import { useDevSettingsStore } from '@shared/stores/devSettingsStore';
import { useGameStateStore } from '@shared/stores';
import { getSettingsByCategory, ConfigCategory, ConfigVisibility } from '@shared/config/utils';
import { CAMERA_CONFIG_SCHEMA } from '@shared/config/settings/camera';
import { useGameStateStore as useGameStateStoreDirect } from '@shared/stores/gameStateStore';

interface SettingsMenuProps {
  scene?: unknown;
}

export default function SettingsMenu({ scene }: SettingsMenuProps = {}) {
  const { settings, updateCameraSettings, resetCameraSettings } = useSettingsStore();
  const { settings: devSettings } = useDevSettingsStore();
  const { returnToMenu, currentScreen } = useGameStateStore();
  const [activeCategory, setActiveCategory] = useState<ConfigCategory>(ConfigCategory.CAMERA);
  const previousSettingsRef = useRef<string>('');

  // Refresh camera config when settings change
  useEffect(() => {
    if (!scene) return;

    const sceneWithMethods = scene as {
      getCameraController?: () => {
        refreshConfig?: () => void;
      } | null;
    };

    const cameraController = sceneWithMethods.getCameraController?.();
    if (cameraController?.refreshConfig) {
      // Create a string representation of current settings to detect changes
      const currentSettings = JSON.stringify({
        player: settings.camera,
        dev: devSettings.camera,
      });

      // Only refresh if settings actually changed
      if (currentSettings !== previousSettingsRef.current) {
        try {
          cameraController.refreshConfig();
          previousSettingsRef.current = currentSettings;
        } catch (error) {
          // Silently handle errors - camera might not be ready yet
          console.warn('Failed to refresh camera config:', error);
        }
      }
    }
  }, [settings.camera, devSettings.camera, scene]);

  // Get player-visible settings grouped by category
  const settingsByCategory = getSettingsByCategory(ConfigVisibility.PLAYER);

  const handleCameraSettingChange = (key: string, value: number) => {
    // Get bounds from dev settings (with safety check)
    const bounds = devSettings?.cameraBounds?.[key as keyof typeof devSettings.cameraBounds];
    if (bounds) {
      // Clamp value to bounds
      const clampedValue = Math.max(bounds.min, Math.min(bounds.max, value));
      updateCameraSettings({ [key]: clampedValue });
    } else {
      updateCameraSettings({ [key]: value });
    }
  };

  const categories = Object.keys(settingsByCategory) as ConfigCategory[];

  return (
    <div className="w-screen h-screen bg-gray-900 flex flex-col items-center justify-center p-8">
      <div className="w-full max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-white">Settings</h2>
          <button
            onClick={() => {
              // If we came from the game (pause menu), go back to game screen
              // Otherwise, return to main menu
              if (currentScreen === 'settings' && scene) {
                // Go back to game screen (game is still paused, will be resumed by pause menu)
                useGameStateStoreDirect.getState().setScreen('game');
              } else {
                returnToMenu();
              }
            }}
            className="px-6 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors"
          >
            {currentScreen === 'settings' && scene ? 'Back to Game' : 'Back to Menu'}
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded transition-colors ${
                activeCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        {/* Settings content */}
        <div className="bg-gray-800 rounded-lg p-6">
          {activeCategory === ConfigCategory.CAMERA && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-white mb-4">Camera Settings</h3>
              {Object.entries(CAMERA_CONFIG_SCHEMA)
                .filter(([_, metadata]) => metadata.visibility === ConfigVisibility.PLAYER)
                .map(([key, metadata]) => (
                  <div key={key} className="space-y-2">
                    <label className="block text-gray-200">
                      <span className="font-medium">{metadata.label}</span>
                      {metadata.description && (
                        <span className="block text-sm text-gray-400 mt-1">
                          {metadata.description}
                        </span>
                      )}
                    </label>
                    <div className="flex items-center gap-4">
                      {(() => {
                        // Get bounds from dev settings, fallback to schema defaults
                        // Safety check: ensure cameraBounds exists
                        const bounds =
                          devSettings?.cameraBounds?.[key as keyof typeof devSettings.cameraBounds];
                        const min = bounds?.min ?? metadata.min ?? 0;
                        const max = bounds?.max ?? metadata.max ?? 100;
                        const step = metadata.step ?? 0.1;
                        const currentValue = settings.camera[
                          key as keyof typeof settings.camera
                        ] as number;
                        return (
                          <>
                            <input
                              type="range"
                              min={min}
                              max={max}
                              step={step}
                              value={currentValue}
                              onChange={(e) =>
                                handleCameraSettingChange(key, parseFloat(e.target.value))
                              }
                              className="flex-1"
                            />
                            <span className="text-white font-mono w-16 text-right">
                              {currentValue}
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                ))}
              <div className="pt-4 border-t border-gray-700">
                <button
                  onClick={resetCameraSettings}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Reset Camera Settings
                </button>
              </div>
            </div>
          )}

          {/* Future: Add other categories (Audio, Graphics, Controls, etc.) */}
        </div>
      </div>
    </div>
  );
}
